import Navbar from "../components/NavbarM";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Button, Form } from "react-bootstrap";
import { ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";

function PointsActionPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const storeId = localStorage.getItem("storeId");
  const storeUid = localStorage.getItem("storeUid");
  const storeName = localStorage.getItem("storeName");

  const { cardNumber, memberData } = location.state || {};

  const [points, setPoints] = useState(memberData?.points ?? 0);
  const [purchaseAmount, setPurchaseAmount] = useState("");
  const [redeemAmount, setRedeemAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const POINTS_PER_DOLLAR = 10;
  const POINTS_NEEDED_FOR_ONE_DOLLAR = 1000;

  const storeMemberId = cardNumber ? `${storeId}_${cardNumber}` : null;

  const validateCardBeforeAction = async () => {
    if (!storeId || !storeUid) {
      throw new Error("Store not logged in. Please sign in again.");
    }

    if (!cardNumber) {
      throw new Error("No card number found.");
    }

    const cardRef = doc(db, "cards", cardNumber);
    const cardSnap = await getDoc(cardRef);

    if (!cardSnap.exists()) {
      throw new Error("This is not a valid PointsHub card.");
    }

    const cardData = cardSnap.data();

    if (cardData.status === "blocked" || cardData.status === "lost") {
      throw new Error("This card is blocked or lost. Please contact admin.");
    }

    return true;
  };

  const playAddSound = () => {
    const audio = new Audio(process.env.PUBLIC_URL + "/sounds/success.mp3");
    audio.volume = 1.0;
    audio.play().catch(() => {});
  };

  const playRedeemSound = () => {
    const audio = new Audio(process.env.PUBLIC_URL + "/sounds/redeem.mp3");
    audio.volume = 1.0;
    audio.play().catch(() => {});
  };

  const redirectHome = () => {
    setTimeout(() => {
      navigate("/Home", { replace: true });
    }, 1200);
  };

  const calculateEarnPoints = () => {
    const amount = Number(purchaseAmount);

    if (!amount || amount <= 0) {
      return 0;
    }

    return Math.round(amount * POINTS_PER_DOLLAR);
  };

  const calculateRedeemPoints = () => {
    const dollars = Number(redeemAmount);

    if (!dollars || dollars <= 0) {
      return 0;
    }

    return Math.round(dollars * POINTS_NEEDED_FOR_ONE_DOLLAR);
  };

  const handleAddPoints = async () => {
    const amountSpent = Number(purchaseAmount);
    const pointsToAdd = calculateEarnPoints();

    if (!amountSpent || amountSpent <= 0) {
      alert("Please enter a valid purchase amount.");
      return;
    }

    if (pointsToAdd <= 0) {
      alert("Purchase amount is too low to earn points.");
      return;
    }

    try {
      setLoading(true);

      await validateCardBeforeAction();

      const memberRef = doc(db, "storeMembers", storeMemberId);
      const transactionRef = doc(collection(db, "transactions"));

      let newPoints = 0;

      await runTransaction(db, async (transaction) => {
        const memberSnap = await transaction.get(memberRef);

        if (!memberSnap.exists()) {
          throw new Error("Store member record not found.");
        }

        const member = memberSnap.data();

        if (member.storeId !== storeId) {
          throw new Error("This member does not belong to your store.");
        }

        if (member.cardNumber !== cardNumber) {
          throw new Error("Card number mismatch.");
        }

        const currentPoints = member.points || 0;
        newPoints = currentPoints + pointsToAdd;

        transaction.update(memberRef, {
          points: newPoints,
          updatedAt: serverTimestamp(),
        });

        transaction.set(transactionRef, {
          storeId,
          storeName: storeName || "",
          storeMemberId,
          cardNumber,
          type: "add",
          points: pointsToAdd,
          balance: newPoints,
          purchaseAmount: amountSpent,
          pointsRate: POINTS_PER_DOLLAR,
          timestamp: serverTimestamp(),
          memberName: member.name || memberData?.name || "Guest",
        });
      });

      setPoints(newPoints);
      setPurchaseAmount("");
      playAddSound();
      alert(`✅ ${pointsToAdd} points added successfully!`);
      redirectHome();
    } catch (err) {
      console.error(err);
      alert(`❌ ${err.message || "Something went wrong while adding points."}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemPoints = async () => {
    const dollarsToRedeem = Number(redeemAmount);
    const pointsToRedeem = calculateRedeemPoints();

    if (!dollarsToRedeem || dollarsToRedeem <= 0) {
      alert("Please enter a valid redemption amount.");
      return;
    }

    if (pointsToRedeem <= 0) {
      alert("Invalid redemption amount.");
      return;
    }

    try {
      setLoading(true);

      await validateCardBeforeAction();

      const memberRef = doc(db, "storeMembers", storeMemberId);
      const transactionRef = doc(collection(db, "transactions"));

      let newPoints = 0;

      await runTransaction(db, async (transaction) => {
        const memberSnap = await transaction.get(memberRef);

        if (!memberSnap.exists()) {
          throw new Error("Store member record not found.");
        }

        const member = memberSnap.data();

        if (member.storeId !== storeId) {
          throw new Error("This member does not belong to your store.");
        }

        if (member.cardNumber !== cardNumber) {
          throw new Error("Card number mismatch.");
        }

        const currentPoints = member.points || 0;

        if (currentPoints < POINTS_NEEDED_FOR_ONE_DOLLAR) {
          throw new Error("Customer needs at least 1000 points to redeem $1.");
        }

        if (currentPoints < pointsToRedeem) {
          const maxRedeem = Math.floor(
            currentPoints / POINTS_NEEDED_FOR_ONE_DOLLAR
          );

          throw new Error(`Not enough points. Customer can redeem up to $${maxRedeem}.`);
        }

        newPoints = currentPoints - pointsToRedeem;

        transaction.update(memberRef, {
          points: newPoints,
          updatedAt: serverTimestamp(),
        });

        transaction.set(transactionRef, {
          storeId,
          storeName: storeName || "",
          storeMemberId,
          cardNumber,
          type: "redeem",
          points: -pointsToRedeem,
          balance: newPoints,
          redeemAmount: dollarsToRedeem,
          redemptionRate: POINTS_NEEDED_FOR_ONE_DOLLAR,
          timestamp: serverTimestamp(),
          memberName: member.name || memberData?.name || "Guest",
        });
      });

      setPoints(newPoints);
      setRedeemAmount("");
      playRedeemSound();
      alert(`✅ $${dollarsToRedeem} redeemed successfully!`);
      redirectHome();
    } catch (err) {
      console.error(err);
      alert(`❌ ${err.message || "Something went wrong while redeeming points."}`);
    } finally {
      setLoading(false);
    }
  };

  const secureCardNumber =
    cardNumber?.replace(/^(\d{4})\d{8,10}(\d{4})$/, "$1********$2") ||
    "Card not found";

  const name = memberData?.name || "Guest";
  const email = memberData?.email || "Not Provided";

  const earnPreview = calculateEarnPoints();
  const redeemPreview = calculateRedeemPoints();
  const maxRedeemDollars = Math.floor(points / POINTS_NEEDED_FOR_ONE_DOLLAR);

  return (
    <>
      <Navbar />

      <div
        className="d-flex justify-content-center align-items-center min-vh-100"
        style={{
          background: "linear-gradient(135deg, #fff4e6, #ffd8a8)",
          padding: "30px",
        }}
      >
        <Card
          className="shadow-lg p-4 text-center"
          style={{
            width: "460px",
            borderRadius: "20px",
            border: "none",
            backgroundColor: "#fff",
          }}
        >
          <h4 className="fw-bold mb-3" style={{ color: "#ff922b" }}>
            Card Confirmed ✅
          </h4>

          <div
            className="p-3 rounded mb-4"
            style={{
              backgroundColor: "#fff9f0",
              border: "2px dashed #ffa94d",
              borderRadius: "12px",
            }}
          >
            <p className="mb-1 text-muted" style={{ fontSize: "14px" }}>
              Member Name
            </p>
            <h5 className="fw-bold mb-2">{name}</h5>

            <p className="mb-1 text-muted" style={{ fontSize: "14px" }}>
              Email
            </p>
            <p className="fw-semibold mb-3">{email}</p>

            <p className="mb-1 text-muted" style={{ fontSize: "14px" }}>
              Card Number
            </p>
            <h6 className="fw-bold mb-3">{secureCardNumber}</h6>

            <p className="mb-1 text-muted" style={{ fontSize: "14px" }}>
              Current Balance This Store
            </p>
            <h4 className="fw-bold text-success">{points} pts</h4>

            <p className="text-muted mt-2 mb-0" style={{ fontSize: "13px" }}>
              Redeemable value: ${maxRedeemDollars}
            </p>
          </div>

          <div
            className="text-start p-3 mb-3"
            style={{
              border: "1px solid #ffe0b2",
              borderRadius: "16px",
              background: "#fffaf4",
            }}
          >
            <h6 className="fw-bold mb-2">Add Points</h6>

            <Form.Label>Purchase Amount</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              placeholder="Example: 25.50"
              value={purchaseAmount}
              onChange={(e) => setPurchaseAmount(e.target.value)}
              disabled={loading}
            />

            <p className="text-muted mt-2 mb-3" style={{ fontSize: "14px" }}>
              Customer will earn: <strong>{earnPreview} points</strong>
            </p>

            <Button
              onClick={handleAddPoints}
              disabled={loading}
              className="w-100"
              style={{
                backgroundColor: "#ff922b",
                border: "none",
                borderRadius: "12px",
                fontWeight: "600",
                color: "white",
                fontSize: "16px",
              }}
            >
              <ArrowUpCircle className="me-2" size={20} />
              Add Points
            </Button>
          </div>

          <div
            className="text-start p-3"
            style={{
              border: "1px solid #ffe0b2",
              borderRadius: "16px",
              background: "#fffaf4",
            }}
          >
            <h6 className="fw-bold mb-2">Redeem Points</h6>

            <Form.Label>Redeem Amount in Dollars</Form.Label>
            <Form.Control
              type="number"
              min="1"
              step="1"
              placeholder="Example: 1"
              value={redeemAmount}
              onChange={(e) => setRedeemAmount(e.target.value)}
              disabled={loading}
            />

            <p className="text-muted mt-2 mb-3" style={{ fontSize: "14px" }}>
              Points required: <strong>{redeemPreview} points</strong>
              <br />
              Available redeemable amount: <strong>${maxRedeemDollars}</strong>
            </p>

            <Button
              onClick={handleRedeemPoints}
              disabled={loading}
              variant="outline-warning"
              className="w-100"
              style={{
                borderRadius: "12px",
                border: "2px solid #fd7e14",
                fontWeight: "600",
                color: "#fd7e14",
                fontSize: "16px",
              }}
            >
              <ArrowDownCircle className="me-2" size={20} />
              Redeem Points
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
}

export default PointsActionPage;